import { useState } from 'react';
import { ChevronDown, X, SlidersHorizontal } from 'lucide-react';
import type { EstateFilters, FilterOptions } from '../types';

interface FilterPanelProps {
  filters: EstateFilters;
  options: FilterOptions | undefined;
  onChange: (filters: EstateFilters) => void;
  isLoading?: boolean;
}

export function FilterPanel({ filters, options, onChange, isLoading }: FilterPanelProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const activeFilterCount = [
    filters.country,
    filters.minBedrooms,
    filters.activities?.length,
    filters.amenities?.length,
  ].filter(Boolean).length;

  const handleCountryChange = (country: string) => {
    onChange({ ...filters, country: country || undefined });
  };

  const handleBedroomsChange = (min: number | undefined) => {
    onChange({ ...filters, minBedrooms: min });
  };

  const handleActivityToggle = (activity: string) => {
    const current = filters.activities || [];
    const updated = current.includes(activity)
      ? current.filter((a) => a !== activity)
      : [...current, activity];
    onChange({ ...filters, activities: updated.length ? updated : undefined });
  };

  const handleAmenityToggle = (amenity: string) => {
    const current = filters.amenities || [];
    const updated = current.includes(amenity)
      ? current.filter((a) => a !== amenity)
      : [...current, amenity];
    onChange({ ...filters, amenities: updated.length ? updated : undefined });
  };

  const clearFilters = () => {
    onChange({});
  };

  if (isLoading || !options) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-sm p-4">
        <div className="h-10 rounded-sm animate-shimmer" />
      </div>
    );
  }

  return (
    <div className="bg-white/5 border border-white/10 rounded-sm overflow-hidden">
      {/* Toggle button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="w-4 h-4 text-foreground" />
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-foreground">
            Filters
          </span>
          {activeFilterCount > 0 && (
            <span className="bg-foreground text-primary text-xs px-2 py-0.5 rounded-full font-semibold">
              {activeFilterCount}
            </span>
          )}
        </div>
        <ChevronDown
          className={`w-4 h-4 text-slate-300 transition-transform ${
            isExpanded ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* Expanded filters */}
      {isExpanded && (
        <div className="px-4 pb-5 space-y-5 border-t border-white/10 pt-5">
          {/* Country select */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-widest text-slate-300 mb-2">
              Country
            </label>
            <select
              value={filters.country || ''}
              onChange={(e) => handleCountryChange(e.target.value)}
              className="w-full px-3 py-2.5 border border-white/15 rounded-sm bg-white/5 text-foreground outline-none transition focus:border-white/30 focus:bg-white/10"
            >
              <option value="" className="bg-primary">All Countries</option>
              {options.countries.map((country) => (
                <option key={country} value={country} className="bg-primary">
                  {country}
                </option>
              ))}
            </select>
          </div>

          {/* Bedrooms slider */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-widest text-slate-300 mb-2">
              Minimum Bedrooms: {filters.minBedrooms || options.bedroomRange.min}
            </label>
            <input
              type="range"
              min={options.bedroomRange.min}
              max={options.bedroomRange.max}
              value={filters.minBedrooms || options.bedroomRange.min}
              onChange={(e) => {
                const value = parseInt(e.target.value);
                handleBedroomsChange(value === options.bedroomRange.min ? undefined : value);
              }}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-slate-400 mt-1">
              <span>{options.bedroomRange.min}</span>
              <span>{options.bedroomRange.max}</span>
            </div>
          </div>

          {/* Activities */}
          {options.activities.length > 0 && (
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-300 mb-2">
                Activities
              </label>
              <div className="flex flex-wrap gap-2">
                {options.activities.slice(0, 10).map((activity) => (
                  <button
                    key={activity}
                    type="button"
                    onClick={() => handleActivityToggle(activity)}
                    className={`text-xs px-3 py-1.5 rounded-sm border transition-all ${
                      filters.activities?.includes(activity)
                        ? 'bg-foreground text-primary border-foreground'
                        : 'bg-white/5 text-slate-300 border-white/15 hover:border-white/30 hover:bg-white/10'
                    }`}
                  >
                    {activity}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Amenities */}
          {options.amenities.length > 0 && (
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-300 mb-2">
                Amenities
              </label>
              <div className="flex flex-wrap gap-2">
                {options.amenities.slice(0, 10).map((amenity) => (
                  <button
                    key={amenity}
                    type="button"
                    onClick={() => handleAmenityToggle(amenity)}
                    className={`text-xs px-3 py-1.5 rounded-sm border transition-all ${
                      filters.amenities?.includes(amenity)
                        ? 'bg-foreground text-primary border-foreground'
                        : 'bg-white/5 text-slate-300 border-white/15 hover:border-white/30 hover:bg-white/10'
                    }`}
                  >
                    {amenity}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Clear button */}
          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={clearFilters}
              className="w-full flex items-center justify-center gap-2 py-2 text-sm text-slate-300 hover:text-foreground hover:bg-white/5 rounded-sm transition-colors"
            >
              <X className="w-4 h-4" />
              Clear all filters
            </button>
          )}
        </div>
      )}
    </div>
  );
}
